import React, { useEffect, useMemo, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";

import { ManagedUser, RoleType } from "store/types";
import { SENIOR_BOWL_COMBINE_DATASET_KEYS } from "config/constants";
import { getTeamData } from "helpers";
import { simplifyRole } from "helpers/customHelpers";
import { useRolesState } from "../../../store/hooks";
import { useUsersDispatch } from "../../../store/hooks";

const emptyUser: ManagedUser = {
  _id: "",
  userid: "",
  firstName: "",
  lastName: "",
  roles: [],
};

const datasetKeys = (role: RoleType): string[] =>
  role.datasets
    .flatMap((d) => d.split(","))
    .map((d) => d.trim())
    .filter((d) => d.length > 0);

const isSeniorBowlCombineRole = (role: RoleType): boolean => {
  const keys = datasetKeys(role);
  return (
    keys.length > 0 &&
    keys.every((k) => SENIOR_BOWL_COMBINE_DATASET_KEYS.includes(k))
  );
};

const roleMatchesTeam = (role: RoleType, teamId: string): boolean =>
  datasetKeys(role).includes(teamId);

const roleBelongsToTeam = (role: RoleType, teamId: string): boolean =>
  role.datasets.length === 1 && role.datasets[0] === teamId;

const hasValue = (value: string): boolean => value.trim().length > 0;

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const getPermissionsText = (user: ManagedUser): string => {
  const allDatasets = user.roles.flatMap((r) => r.datasets);
  if (allDatasets.includes("*")) return "All Teams";
  const teamIds = [...new Set(allDatasets.flatMap((d) => d.split(",")))];
  const teamNames = teamIds.map((id) => {
    const team = getTeamData(id);
    return team ? team.teamNick : id;
  });
  return teamNames.join(", ");
};

const UserModal = ({
  show,
  onClose,
  selectedUser,
  teamId,
  isCommandCenter,
}: {
  show: boolean;
  onClose: () => void;
  selectedUser: ManagedUser | null;
  teamId: string;
  isCommandCenter: boolean;
}) => {
  const roles = useRolesState();
  const { createUser, updateUser } = useUsersDispatch();

  const filteredRoles = useMemo(
    () =>
      teamId === "*"
        ? roles
        : teamId === "10*"
        ? roles.filter(isSeniorBowlCombineRole)
        : roles.filter((r) =>
            isCommandCenter
              ? roleMatchesTeam(r, teamId)
              : roleBelongsToTeam(r, teamId)
          ),
    [roles, teamId, isCommandCenter]
  );

  const sortedRoles = useMemo(() => {
    return [...filteredRoles].sort((a, b) => {
      const labelA = isCommandCenter ? a.role : simplifyRole(a.role);
      const labelB = isCommandCenter ? b.role : simplifyRole(b.role);
      return (
        labelA.localeCompare(labelB, undefined, { sensitivity: "base" }) ||
        a.role.localeCompare(b.role, undefined, { sensitivity: "base" })
      );
    });
  }, [filteredRoles, isCommandCenter]);

  const resolveSelectedRoleId = (
    selectedUser: ManagedUser | null,
    availableRoles: RoleType[]
  ): string => {
    if (selectedUser) {
      return selectedUser.roles[0]?._id || "";
    }

    return availableRoles[0]?._id || "";
  };

  const [user, setUser] = useState<ManagedUser>(
    selectedUser ? { ...selectedUser } : emptyUser
  );
  const [selectedRoleId, setSelectedRoleId] = useState<string>(
    resolveSelectedRoleId(selectedUser, sortedRoles)
  );

  // Resets on every open; the modal stays mounted, so its state would survive otherwise.
  useEffect(() => {
    if (!show) return;
    setUser(selectedUser ? { ...selectedUser } : emptyUser);
    setSelectedRoleId(resolveSelectedRoleId(selectedUser, sortedRoles));
  }, [show, selectedUser, sortedRoles]);

  const selectedRole = filteredRoles.find((r) => r._id === selectedRoleId);
  const isSaveDisabled =
    !hasValue(user.firstName) ||
    !hasValue(user.lastName) ||
    !isValidEmail(user.userid) ||
    !selectedRole;

  const handleSave = () => {
    if (isSaveDisabled || !selectedRole) return;

    const userWithRole = {
      ...user,
      firstName: user.firstName.trim(),
      lastName: user.lastName.trim(),
      userid: user.userid.trim(),
      roles: [selectedRole],
    };
    if (selectedUser) {
      updateUser(userWithRole);
    } else {
      createUser(userWithRole);
    }
    onClose();
  };

  const updateField =
    (field: "firstName" | "lastName" | "userid") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setUser((prev) => ({ ...prev, [field]: value }));
    };

  const labelClassName = (value: string): string =>
    `material-label${hasValue(value) ? " material-label-filled" : ""}`;

  const selectedRoleName =
    filteredRoles.find((r) => r._id === selectedRoleId)?.role ?? "";

  const selectedRoleLabel =
    selectedRoleName && !isCommandCenter
      ? simplifyRole(selectedRoleName)
      : selectedRoleName;

  return (
    <Modal
      className="modal-content-05"
      show={show}
      onHide={onClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {selectedUser ? "Edit User" : "Add User"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedUser && isCommandCenter && (
          <div
            style={{
              fontSize: "13px",
              fontWeight: "bold",
              marginBottom: "15px",
            }}
          >
            Permissions: {getPermissionsText(selectedUser)}
          </div>
        )}
        <div className="material-textbox" id="first-name-input">
          <input
            className="material-input"
            required={true}
            type={"text"}
            value={user.firstName}
            onChange={updateField("firstName")}
          />
          <span className={labelClassName(user.firstName)}>First Name</span>
        </div>
        <div className="material-textbox" id="last-name-input">
          <input
            className="material-input"
            required={true}
            type={"text"}
            value={user.lastName}
            onChange={updateField("lastName")}
          />
          <span className={labelClassName(user.lastName)}>Last Name</span>
        </div>
        <div className="material-textbox" id="email-input">
          <input
            className="material-input"
            required={true}
            type={"email"}
            value={user.userid}
            onChange={updateField("userid")}
          />
          <span className={labelClassName(user.userid)}>Email Address</span>
        </div>
        <div className="material-textbox">
          <p>Choose Application Role</p>
          <Dropdown className="btn-90">
            <Dropdown.Toggle id="dropdown-basic" className="btn-gray-l">
              {selectedRoleLabel}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {sortedRoles.map((role) => (
                <Dropdown.Item
                  key={role._id}
                  onClick={() => setSelectedRoleId(role._id)}
                >
                  {isCommandCenter ? role.role : simplifyRole(role.role)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </Modal.Body>
      <Modal.Footer className="text-right footer-s">
        <button className="btn-transparet" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn btn-blue"
          disabled={isSaveDisabled}
          style={isSaveDisabled ? { opacity: 0.6 } : {}}
          onClick={handleSave}
        >
          {selectedUser ? "Save" : "Create"}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserModal;
